import React, { useCallback, useRef, useState } from 'react';
import { compose, identity, pick, prop } from 'ramda';
import { useUserSelector } from 'js/store/hooks';
import { Loader as CommonLoader } from 'js/components/common/Loader';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useTranslations } from 'js/hooks/i18n';
import { useGetErrorMessage } from 'js/hooks/server/hooks';

import { PortletContext, usePortletConfig, usePortletDispatch, withDynamicReducer } from '../store/context';
import { portletFetchFailAction, portletFetchSuccessAction } from '../store/actions';

export const withForLoggedIn = Component => (props) => {
    const { isLoggedIn, role } = useUserSelector(pick(['isLoggedIn', 'role']));
    const t = useTranslations('playtech.mobile.system.portlet.portletIsUnavailable.');

    if (!isLoggedIn) {
        return t(role);
    }

    return <Component {...props} />;
};

export const withPortletContext = ({ parseCms = identity }) => PortletComponent => ({ config = {}, ...props }) => {
    const { reactPortletId, portletId, portletType } = config;
    const parsedConfig = parseCms(config);
    const newConfig = { ...parsedConfig, reactPortletId, portletId, portletType };

    return (
        <PortletContext.Provider value={newConfig}>
            <PortletComponent {...props} />
        </PortletContext.Provider>
    );
};

export const parseData = prop('data');

export const useWrappedCallbacks = ({ onResponse, onError, fetchPortlet }) => {
    const dispatch = usePortletDispatch();
    const { tkPrefix } = usePortletConfig();
    const getErrorMessage = useGetErrorMessage({ tkPrefix });

    return {
        onResponse: useCallback(
            (response) => {
                const result = onResponse(response);

                // This will put parsed response and fetch method to portlet context
                // if that portlet's reducer handles this action
                dispatch?.(portletFetchSuccessAction({
                    response: result,
                    fetchPortlet
                }));

                return result;
            },
            [dispatch, onResponse, fetchPortlet]
        ),
        onError: useCallback(
            (error) => {
                const result = onError
                    ? onError(error, { tkPrefix })
                    : getErrorMessage(error);

                dispatch?.(portletFetchFailAction({
                    error: result,
                    fetchPortlet
                }));

                return result;
            },
            [dispatch, tkPrefix, onError, fetchPortlet, getErrorMessage]
        )
    };
};

const DEFAULT_SHOW_LOADER = true;
const DEFAULT_SHOW_ERROR = true;

const useWrappedFetch = ({ fetchRef, setShowLoader, setShowError }) => useCallback(
    (fetchParams, options = {}) => {
        const {
            showLoader = DEFAULT_SHOW_LOADER,
            showError = DEFAULT_SHOW_ERROR
        } = options;

        setShowLoader(showLoader);
        setShowError(showError);
        fetchRef.current(fetchParams);
    },
    [fetchRef, setShowLoader, setShowError]
);

/*
Currently:
 1. Portlet context doesn't contain `error` or `isLoading` state.
 */
export const withPortletFetch = ({
    fetch,
    useParams = () => undefined,
    onResponse = parseData,
    onError,
    Loader = CommonLoader,
    Error = ErrorMessage,
    showLoader: showLoaderInitial = DEFAULT_SHOW_LOADER,
    showError: showErrorInitial = DEFAULT_SHOW_ERROR
}) => PortletComponent => (props) => {
    const [showLoader, setShowLoader] = useState(showLoaderInitial);
    const [showError, setShowError] = useState(showErrorInitial);
    const fetchRef = useRef();
    const params = useParams(props);
    const fetchPortlet = useWrappedFetch({ fetchRef, setShowLoader, setShowError });
    const [{ isLoading, error, data }, refetch] =
        useDataFetch({ fetch, params, ...useWrappedCallbacks({ onResponse, onError, fetchPortlet }) });

    fetchRef.current = refetch;

    if (showLoader && isLoading) {
        return <Loader {...props} />;
    }

    if (showError && error) {
        return <Error {...props} message={error} />;
    }

    return <PortletComponent {...{ ...props, fetchPortlet, isLoading, error, data }} />;
};

export const withPortlet = ({
    forLoggedIn,

    parseCms,

    reducer,
    initialState,
    name = 'Portlet',

    fetch,
    useParams,
    onResponse,
    onError,
    Loader,
    Error
} = {}) => {
    const enhancers = [];

    if (forLoggedIn) {
        enhancers.push(withForLoggedIn);
    }

    // r/o context with portlet CMS config
    enhancers.push(withPortletContext({ parseCms }));

    if (reducer) {
        // r/w context with portlet reducer
        enhancers.push(withDynamicReducer({ reducer, initialState, reducerName: name }));
    }

    if (fetch) {
        enhancers.push(withPortletFetch({ fetch, useParams, onResponse, onError, Loader, Error }));
    }

    switch (enhancers.length) {
        case 0:
            return identity;
        case 1:
            return enhancers[0];
        default:
            return compose(...enhancers);
    }
};
